import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  BookOpen,
  Wrench,
  Award,
  BookMarked,
  Landmark,
  User,
  Menu,
  X,
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/courtyard', label: 'Courtyard', icon: Landmark },
  { path: '/evenings', label: 'Evenings', icon: BookOpen },
  { path: '/toolkit', label: 'Toolkit', icon: Wrench },
  { path: '/resources', label: 'Resources', icon: BookMarked },
  { path: '/achievements', label: 'Achievements', icon: Award },
  { path: '/profile', label: 'Profile', icon: User },
];

const Navigation = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="bg-white/90 backdrop-blur-sm border-b border-courtyard-gold/20 sticky top-0 z-50" role="navigation" aria-label="Main navigation">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo: the app's icon beside its name, as in every DDD app header.
              The picture is decorative here (the name is right beside it), so
              it carries an empty alt and the link is named by its text. */}
          <Link to="/dashboard" className="flex items-center gap-3 flex-shrink-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-courtyard-clay focus-visible:ring-offset-2">
            <img
              src="/images/logos/finding-your-voice-nav.webp"
              alt=""
              width="40"
              height="40"
              className="w-10 h-10 rounded-xl border border-courtyard-gold/40 shadow-courtyard-sm flex-shrink-0"
            />
            <span className="flex flex-col leading-tight">
              <span className="font-display text-lg font-bold text-courtyard-stone whitespace-nowrap">
                Finding Your Voice
              </span>
              <span className="hidden sm:block text-xs font-medium text-courtyard-clay">
                Speaking confidence
              </span>
            </span>
          </Link>

          {/* Desktop nav. Seven items beside the icon and name need about 950px,
              so the compact menu takes over below the lg breakpoint. */}
          <div className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
              const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
              return (
                <Link
                  key={path}
                  to={path}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex items-center gap-1.5 px-2 xl:px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? 'bg-courtyard-gold/15 text-courtyard-clay'
                      : 'text-courtyard-stone/80 hover:text-courtyard-stone hover:bg-courtyard-gold/5'
                  }`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  {label}
                </Link>
              );
            })}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-courtyard-stone/80 hover:text-courtyard-stone"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {mobileOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            id="mobile-nav"
            className="lg:hidden overflow-hidden bg-white border-t border-courtyard-gold/10"
          >
            <div className="px-4 py-2 space-y-1">
              {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
                const isActive = location.pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setMobileOpen(false)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                      isActive
                        ? 'bg-courtyard-gold/15 text-courtyard-clay'
                        : 'text-courtyard-stone/80 hover:bg-courtyard-gold/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" aria-hidden="true" />
                    {label}
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
